/** Read helpers over a Graph: siblings, visible order, key slots. Pure; no writes. */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import { keysBetween } from './order.ts'
import type { Rand } from './rng.ts'

export function liveChildren(g: Graph, id: NodeId): NodeId[] {
  const out: NodeId[] = []
  for (const k of g.children(id)) if (g.isLive(k)) out.push(k)
  return out
}

export function hasLiveChildren(g: Graph, id: NodeId): boolean {
  for (const k of g.children(id)) if (g.isLive(k)) return true
  return false
}

export function firstLiveChild(g: Graph, id: NodeId): NodeId | null {
  for (const k of g.children(id)) if (g.isLive(k)) return k
  return null
}

export function lastLiveChild(g: Graph, id: NodeId): NodeId | null {
  const ks = g.children(id)
  for (let i = ks.length - 1; i >= 0; i--) if (g.isLive(ks[i]!)) return ks[i]!
  return null
}

function siblingsOf(g: Graph, id: NodeId): readonly NodeId[] {
  const p = g.must(id).parentId
  return p === null ? [] : g.children(p)
}

export function prevLiveSibling(g: Graph, id: NodeId): NodeId | null {
  const s = siblingsOf(g, id)
  for (let i = s.indexOf(id) - 1; i >= 0; i--) if (g.isLive(s[i]!)) return s[i]!
  return null
}

export function nextLiveSibling(g: Graph, id: NodeId): NodeId | null {
  const s = siblingsOf(g, id)
  const at = s.indexOf(id)
  if (at < 0) return null
  for (let i = at + 1; i < s.length; i++) if (g.isLive(s[i]!)) return s[i]!
  return null
}

export function liveSiblingsAfter(g: Graph, id: NodeId): NodeId[] {
  const s = siblingsOf(g, id)
  const out: NodeId[] = []
  for (let i = s.indexOf(id) + 1; i < s.length; i++) if (g.isLive(s[i]!)) out.push(s[i]!)
  return out
}

/** Collapsed only counts when there is something to hide. */
export function isFolded(g: Graph, id: NodeId): boolean {
  return g.must(id).collapsed && hasLiveChildren(g, id)
}

/** Last node visible inside `id`'s subtree (itself if folded or a leaf). */
export function lastVisibleDescendant(g: Graph, id: NodeId): NodeId {
  let cur = id
  for (;;) {
    if (g.must(cur).collapsed) return cur
    const last = lastLiveChild(g, cur)
    if (last === null) return cur
    cur = last
  }
}

/** True if `anc` is a strict ancestor of `id`. */
export function isAncestor(g: Graph, anc: NodeId, id: NodeId): boolean {
  for (let cur = g.must(id).parentId; cur !== null; cur = g.must(cur).parentId) {
    if (cur === anc) return true
  }
  return false
}

/** Depth of `id` below `root` (children of root = 0), or -1 if not inside root. */
export function depthBelow(g: Graph, root: NodeId, id: NodeId): number {
  let d = 0
  for (let cur = g.must(id).parentId; cur !== null; cur = g.must(cur).parentId) {
    if (cur === root) return d
    d++
  }
  return -1
}

export interface Row {
  readonly id: NodeId
  readonly depth: number
}

/** The visible outline under `root`: pre-order, live nodes, not descending into collapsed ones. */
export function visibleRows(g: Graph, root: NodeId): Row[] {
  const out: Row[] = []
  const walk = (id: NodeId, depth: number): void => {
    for (const k of g.children(id)) {
      const n = g.get(k)!
      if (n.deletedAt !== null) continue
      out.push({ id: k, depth })
      if (!n.collapsed) walk(k, depth + 1)
    }
  }
  walk(root, 0)
  return out
}

/** Visually previous block within `root`'s view, or null at the top. */
export function prevVisible(g: Graph, root: NodeId, id: NodeId): NodeId | null {
  if (id === root) return null
  const s = prevLiveSibling(g, id)
  if (s !== null) return lastVisibleDescendant(g, s)
  const p = g.must(id).parentId
  return p === null || p === root ? null : p
}

/** Visually next block within `root`'s view, or null at the bottom. */
export function nextVisible(g: Graph, root: NodeId, id: NodeId): NodeId | null {
  // The view root's own collapsed flag is ignored: zooming in always shows its children.
  if (id === root || !g.must(id).collapsed) {
    const c = firstLiveChild(g, id)
    if (c !== null) return c
  }
  for (let cur: NodeId = id; cur !== root; ) {
    const s = nextLiveSibling(g, cur)
    if (s !== null) return s
    const p = g.must(cur).parentId
    if (p === null) return null
    cur = p
  }
  return null
}

// ------------------------------------------------------------------ key slots
// All slots are computed against the FULL children list (tombstones included).

function orderOf(g: Graph, id: NodeId | undefined): string | null {
  return id === undefined ? null : g.must(id).order
}

/** n keys right after `afterId` (and before whatever entry follows it). */
export function keysAfter(g: Graph, parentId: NodeId, afterId: NodeId, n: number, rand: Rand): string[] {
  const ks = g.children(parentId)
  const i = ks.indexOf(afterId)
  if (i < 0) throw new Error(`keysAfter: ${afterId} not under ${parentId}`)
  return keysBetween(orderOf(g, afterId), orderOf(g, ks[i + 1]), n, rand)
}

/** n keys right before `beforeId`. */
export function keysBefore(g: Graph, parentId: NodeId, beforeId: NodeId, n: number, rand: Rand): string[] {
  const ks = g.children(parentId)
  const i = ks.indexOf(beforeId)
  if (i < 0) throw new Error(`keysBefore: ${beforeId} not under ${parentId}`)
  return keysBetween(orderOf(g, ks[i - 1]), orderOf(g, beforeId), n, rand)
}

export function keysAtStart(g: Graph, parentId: NodeId, n: number, rand: Rand): string[] {
  return keysBetween(null, orderOf(g, g.children(parentId)[0]), n, rand)
}

export function keysAtEnd(g: Graph, parentId: NodeId, n: number, rand: Rand): string[] {
  const ks = g.children(parentId)
  return keysBetween(orderOf(g, ks[ks.length - 1]), null, n, rand)
}
